// Bell System API client.
// Wraps the REST endpoints from the Postman collection. Keeps all transport /
// auth concerns here so UI components stay free of business logic (CLAUDE.md).
//
// Standard response envelope (API Standards, CLAUDE.md):
//   { success: boolean, data?: T, error?: string, meta?: object }

import { clearSession, getToken } from "./auth";

// Trim before stripping the trailing slash — env files often carry stray
// whitespace, which would otherwise land inside the request URL.
const BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL?.trim().replace(/\/$/, "")
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  meta?: PageMeta & Record<string, unknown>;
  /** Admin list endpoints return pagination alongside `data`. */
  pagination?: PageMeta;
}

export interface PageMeta {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
  hasNext?: boolean;
  hasPrev?: boolean;
}

/** Roles the create/edit form may assign. The API's create endpoint accepts
 *  exactly `admin` (Sales Manager) and `sales` (Salesperson) — `moderator` is
 *  provisioned out-of-band, so it is display-only (see ROLE_LABELS). */
export const USER_ROLES = [
  { value: "sales", label: "Salesperson" },
  { value: "admin", label: "Sales Manager" },
] as const;

/** Display labels for every role the API may return, including ones the form
 *  cannot assign. `role` stays a string so new roles never break the client. */
const ROLE_LABELS: Record<string, string> = {
  sales: "Salesperson",
  admin: "Sales Manager",
  moderator: "Super Admin",
};

export function roleLabel(role: string | undefined | null): string {
  if (!role) return "—";
  return ROLE_LABELS[role] ?? role;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  roles?: string[];
  phone?: string | null;
  mobile?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipcode?: string | null;
  note?: string | null;
  dateOfBirth?: string | null;
  /** Server-relative upload path, e.g. `/uploads/profile-pictures/x.png`. */
  profileImage?: string | null;
  accountStatus?: string;
  isActive?: boolean;
  isVerified?: boolean;
  status?: string;
  storeId?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Minimal identity persisted client-side after login. */
export interface AuthUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role: string;
  profileImage?: string | null;
}

/** Absolute URL for a server-relative upload path. */
export function assetUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : `${BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

export interface LoginResult {
  token: string;
  user?: AuthUser;
}

export interface CreateUserInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: string;
}

/** `role` is intentionally absent: PUT /api/users/{id} rejects unrecognized
 *  keys, and role is assignable at creation only. */
export type UpdateUserInput = Partial<Omit<CreateUserInput, "password" | "role">> & {
  phone?: string;
  mobile?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipcode?: string;
  status?: string;
};

export interface ListUsersParams {
  search?: string;
  /** Narrows to a single role (e.g. `admin`, `sales`). Omit for all roles. */
  role?: string;
  page?: number;
  limit?: number;
}

export interface Paginated<T> {
  items: T[];
  meta: PageMeta;
}

// ---------------------------------------------------------------------------
// Error
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// ---------------------------------------------------------------------------
// Core request
// ---------------------------------------------------------------------------

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** JSON body, or a FormData for multipart uploads (boundary set by fetch). */
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  auth?: boolean; // attach bearer token (default true)
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<ApiEnvelope<T>> {
  const { method = "GET", body, query, auth = true, signal } = opts;
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;

  const url = new URL(`${BASE_URL}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {};
  if (body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(
      "Cannot reach the server. Check your connection and try again.",
      0,
    );
  }

  // 204 / empty body
  const text = await res.text();
  let json: ApiEnvelope<T> | null = null;
  if (text) {
    try {
      json = JSON.parse(text) as ApiEnvelope<T>;
    } catch {
      json = null;
    }
  }

  if (res.status === 401 && auth) {
    // Token invalid/expired -> drop the client session.
    clearSession();
  }

  if (!res.ok || (json && json.success === false)) {
    const msg =
      json?.error ||
      json?.message ||
      `Request failed (${res.status})`;
    throw new ApiError(msg, res.status);
  }

  return json ?? ({ success: true } as ApiEnvelope<T>);
}

/** Normalize a list payload into { items, meta }. The API may return the array
 *  directly under `data`, or as `data.items` / `data.users`, with pagination in
 *  either `meta` or alongside the array. */
function toPaginated<T>(env: ApiEnvelope<unknown>): Paginated<T> {
  const d = env.data as unknown;
  let items: T[] = [];
  let inlineMeta: PageMeta = {};

  if (Array.isArray(d)) {
    items = d as T[];
  } else if (d && typeof d === "object") {
    const obj = d as Record<string, unknown>;
    const arr =
      (obj.items as T[]) ?? (obj.users as T[]) ?? (obj.results as T[]) ?? [];
    items = Array.isArray(arr) ? arr : [];
    inlineMeta = {
      page: obj.page as number | undefined,
      limit: obj.limit as number | undefined,
      total: obj.total as number | undefined,
      totalPages: obj.totalPages as number | undefined,
    };
  }

  return {
    items,
    meta: { ...inlineMeta, ...(env.pagination ?? {}), ...(env.meta ?? {}) },
  };
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

/** Editable profile fields. Blank strings are rejected by the API, so callers
 *  must omit a field rather than send "" to clear it. */
export interface ProfileInput {
  firstName?: string;
  lastName?: string;
  phone?: string;
  mobile?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipcode?: string;
  note?: string;
  dateOfBirth?: string; // YYYY-MM-DD
}

/** Drops empty/undefined values — see ProfileInput. */
function compact(input: object): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (typeof v === "string" ? v.trim() !== "" : v !== undefined && v !== null) out[k] = v;
  }
  return out;
}

export const authApi = {
  login(email: string, password: string, signal?: AbortSignal) {
    return request<LoginResult>("/api/users/login", {
      method: "POST",
      body: { email, password },
      auth: false,
      signal,
    });
  },

  /** Server-side sign-out. The JWT stays valid (stateless), but the event is
   *  audited — always clear the client session afterwards regardless. */
  logout() {
    return request<null>("/api/users/logout", { method: "POST" });
  },

  profile(signal?: AbortSignal) {
    return request<User>("/api/users/profile", { signal });
  },

  async updateProfile(input: ProfileInput): Promise<User | null> {
    const env = await request<User>("/api/users/profile", {
      method: "PUT",
      body: compact(input),
    });
    return env.data ?? null;
  },

  /** PUT /api/users/profile-picture — multipart, field name `profilePicture`. */
  async updateProfilePicture(file: File): Promise<User | null> {
    const form = new FormData();
    form.append("profilePicture", file);
    const env = await request<User>("/api/users/profile-picture", {
      method: "PUT",
      body: form,
    });
    return env.data ?? null;
  },

  changePassword(oldPassword: string, newPassword: string) {
    return request<null>("/api/users/change-password", {
      method: "PUT",
      body: { oldPassword, newPassword },
    });
  },

  // --- Password reset (OTP) ------------------------------------------------
  // 1. forgotPassword -> emails an OTP (always succeeds; no account probing)
  // 2. verifyResetOtp -> marks the email as verified server-side
  // 3. resetPassword  -> rejected until step 2 has run for that email

  forgotPassword(email: string) {
    return request<null>("/api/users/forgot-password", {
      method: "POST",
      body: { email },
      auth: false,
    });
  },

  verifyResetOtp(email: string, otp: string) {
    return request<null>("/api/users/verify-reset-otp", {
      method: "POST",
      body: { email, otp },
      auth: false,
    });
  },

  resendOtp(email: string) {
    return request<null>("/api/users/resend-otp", {
      method: "POST",
      body: { email },
      auth: false,
    });
  },

  resetPassword(email: string, newPassword: string) {
    return request<null>("/api/users/reset-password", {
      method: "POST",
      body: { email, newPassword },
      auth: false,
    });
  },
};

// ---------------------------------------------------------------------------
// Push tokens
// ---------------------------------------------------------------------------

/** Device platforms the API accepts for a push registration. The dashboard only
 *  ever sends WEB; the mobile apps own the others. */
export type PushPlatform = "WEB" | "IOS" | "ANDROID";

export const pushTokensApi = {
  /** POST /api/users/push-tokens — binds an FCM token to the caller's account.
   *  The server de-duplicates by token, so re-sending is safe. */
  register(token: string, platform: PushPlatform = "WEB") {
    return request<null>("/api/users/push-tokens", {
      method: "POST",
      body: { token, platform },
    });
  },

  /** DELETE /api/users/push-tokens — unbinds the token so a signed-out browser
   *  stops receiving this user's alerts. Must run before the JWT is cleared. */
  unregister(token: string) {
    return request<null>("/api/users/push-tokens", {
      method: "DELETE",
      body: { token },
    });
  },
};

// ---------------------------------------------------------------------------
// User administration
// ---------------------------------------------------------------------------

export const usersApi = {
  async list(params: ListUsersParams = {}, signal?: AbortSignal): Promise<Paginated<User>> {
    const env = await request<unknown>("/api/users", {
      query: {
        search: params.search,
        role: params.role,
        page: params.page,
        limit: params.limit,
      },
      signal,
    });
    return toPaginated<User>(env);
  },

  async getById(id: string, signal?: AbortSignal): Promise<User> {
    const env = await request<User>(`/api/users/${id}`, { signal });
    if (!env.data) throw new ApiError("User not found", 404);
    return env.data;
  },

  async create(input: CreateUserInput): Promise<User | null> {
    const env = await request<User>("/api/users", {
      method: "POST",
      body: input,
    });
    return env.data ?? null;
  },

  async update(id: string, input: UpdateUserInput): Promise<User | null> {
    const env = await request<User>(`/api/users/${id}`, {
      method: "PUT",
      body: input,
    });
    return env.data ?? null;
  },

  async remove(id: string): Promise<void> {
    await request<null>(`/api/users/${id}`, { method: "DELETE" });
  },
};

// ---------------------------------------------------------------------------
// Admin (GM / Dealer Principal scope)
// ---------------------------------------------------------------------------

/** Metrics the API cannot compute yet arrive as an availability envelope
 *  instead of a number. Never render the raw object — use `metric()`. */
export interface MetricEnvelope {
  value: number | null;
  available: boolean;
  reason?: string;
}

export type Metric = number | null | MetricEnvelope;

export function metric(m: Metric | undefined): MetricEnvelope {
  if (typeof m === "number") return { value: m, available: true };
  if (m && typeof m === "object") return m;
  return { value: null, available: false, reason: "Not reported" };
}

/** Numeric value or `fallback` when the API reports the metric unavailable. */
export function metricOr(m: Metric | undefined, fallback = 0): number {
  const { value, available } = metric(m);
  return available && value !== null ? value : fallback;
}

export type AdminDealStatus = "ACTIVE" | "SOLD" | "LOST" | "PENDING_FOLLOW_UP";

export interface NamedRef {
  id: number;
  firstName: string;
  lastName: string;
  managerId?: number | null;
}

export interface AdminDashboard {
  traffic: {
    totalCustomersLogged: number;
    activeDeals: number;
    dealsClosedToday: number;
    pendingFollowUps: number;
  };
  salesPerformance: {
    totalDealsSold: number;
    totalDealsLost: number;
    conversionRate: number;
    engagementToCloseRate: Metric;
  };
  managerPerformance: {
    averageResponseTimeMinutes: Metric;
    engagementPercentage: Metric;
    totalEscalatedDeals: number;
    totalIgnoredDeals: Metric;
  };
  bellPerformance: {
    totalBellEvents: number;
    averageBellsPerDeal: number;
    averageBellsPerSoldDeal: number;
    averageBellsPerLostDeal: number;
  };
  financial: {
    totalGross: Metric;
    averageGrossPerDeal: Metric;
    grossPerBell: Metric;
    lowGrossFlaggedDeals: Metric;
  };
  pipeline: {
    active: number;
    negotiating: Metric;
    waitingForManager: number;
    escalated: number;
    sold: number;
    lost: number;
    followUp: number;
  };
  operationalAlerts: {
    customerLeaving: AdminAlertItem[];
    highBellCount: { items: AdminAlertItem[]; available: boolean; reason?: string };
  };
  metadata: {
    scope?: string;
    reportingTimezone?: string;
    dateFrom?: string;
    dateTo?: string;
    outcomeCountsScope?: string;
    limitations?: string[];
  };
}

/** Alert rows are sparse — the API only fills what it knows for that type. */
export interface AdminAlertItem {
  id?: number | string;
  dealId?: number;
  dealCode?: string;
  code?: string;
  customerName?: string;
  bellCount?: number;
  salesperson?: NamedRef | null;
  manager?: NamedRef | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminPipeline {
  byStatus: Record<string, number>;
  byStage: Record<string, number>;
  waitingForManager: number;
  escalated: number;
}

export interface AdminFilters {
  managers: NamedRef[];
  salespeople: NamedRef[];
  dealStatuses: string[];
  dealStages?: string[];
  bellTypes: string[];
  priorities?: string[];
  customers?: { id: number; name: string }[];
  reportingGroups?: string[];
  reportingTimezone?: string;
}

export interface AdminDeal {
  id: number;
  code: string;
  customerName: string | null;
  vehicle: string | null;
  salesperson: NamedRef | null;
  manager: NamedRef | null;
  status: AdminDealStatus;
  currentStage: string | null;
  bellCount: number;
  priority: string | null;
  isCustomerLeaving: boolean;
  checkInAt: string | null;
  createdAt: string;
  closedAt: string | null;
}

export interface AdminBellEvent {
  id: number;
  type: string;
  triggeredBy: NamedRef | null;
  note: string | null;
  responseTime: number | null;
  responseTimeMeaning: string | null;
  createdAt: string;
}

export interface AdminTimelineEntry {
  id: string;
  type: string;
  label: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface AdminDealDetail {
  deal: {
    id: number;
    code: string;
    status: AdminDealStatus;
    currentStage: string | null;
    priority: string | null;
    vehicle: string | null;
    tradeIn: boolean;
    tradeInInformation: string | null;
    note: string | null;
    managerNote: string | null;
    followUpNotes: string | null;
    reminderDate: string | null;
    /** Decimal string from the API, e.g. "50000". Null until gross is entered. */
    grossProfit?: number | string | null;
    bellCount: number;
    isCustomerLeaving: boolean;
    checkInAt: string | null;
    engagedAt: string | null;
    createdAt: string;
    updatedAt: string;
    closedAt: string | null;
  };
  customer: {
    id: number;
    name: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
    profileImage: string | null;
  } | null;
  salesperson: (NamedRef & { email?: string | null; phone?: string | null }) | null;
  manager: (NamedRef & { email?: string | null; phone?: string | null }) | null;
  timeline: AdminTimelineEntry[];
  bellTimeline: AdminBellEvent[];
  documents: { id: number; name?: string; url?: string; createdAt?: string }[];
  latestWorksheet: Record<string, unknown> | null;
}

export interface AdminCustomer {
  id: number;
  name: string | null;
  phone: string | null;
  email: string | null;
  profileImage: string | null;
  dealCount: number;
  activeDealCount: number;
  totalBellCount: number;
  latestDeal: { id: number; code: string; status: AdminDealStatus; createdAt: string } | null;
  createdAt: string;
}

export interface AdminCustomerDetail {
  customer: {
    id: number;
    name: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
    profileImage: string | null;
    createdAt: string;
  };
  driverLicense: { imageUrl?: string; number?: string; scannedAt?: string } | null;
  dealHistory: AdminDeal[];
  currentActiveDeal: AdminDeal | null;
}

/** Deal sub-resources. Shapes are sparse in the current dataset, so fields stay
 *  optional and the UI renders what the server actually sends. */
export interface DealDocument {
  id: number;
  name?: string;
  fileName?: string;
  url?: string;
  filePath?: string;
  mimeType?: string;
  uploadedBy?: NamedRef | null;
  createdAt?: string;
}

export interface DealWorksheet {
  id: number;
  status?: string;
  salePrice?: number | string;
  tradeIn?: number | string;
  downPayment?: number | string;
  interestRate?: number;
  financeMonths?: number;
  monthlyPayment?: number | string;
  frontGross?: number | string;
  backGross?: number | string;
  note?: string | null;
  createdBy?: NamedRef | null;
  reviewedBy?: NamedRef | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface DealCounterOffer {
  id: number;
  status?: string;
  offerPrice?: number | string;
  monthlyPayment?: number | string;
  downPayment?: number | string;
  tradeInValue?: number | string;
  note?: string | null;
  createdBy?: NamedRef | null;
  reviewedBy?: NamedRef | null;
  createdAt?: string;
}

/** Fields PUT /api/admin/deals/{id} accepts — anything else is rejected. */
export interface UpdateDealInput {
  status?: AdminDealStatus;
  priority?: string;
  managerId?: number;
  managerNote?: string;
}

/** POST body for creating a manager or salesperson (no role key: the endpoint
 *  determines the role). */
export interface CreateStaffInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export type UpdateStaffInput = Partial<Omit<CreateStaffInput, "password">> & {
  phone?: string;
};

export interface ManagerDetail extends StaffMember {
  notificationPreferences?: Record<string, boolean>;
}

export interface ManagerIndividualAnalytics {
  manager: NamedRef;
  summary: {
    assignedSalespeople: number;
    assignedDeals: number;
    activeDeals: number;
    pendingDeals: number;
    soldDeals: number;
    lostDeals: number;
    completedDeals: number;
    closeRate: number;
    activeBellCount: number;
    counterOfferActions: number;
    worksheetReviews: number;
    responseCount: number;
    averageResponseTimeMinutes: number | null;
  };
}

export interface SalespersonIndividualAnalytics {
  salesperson: NamedRef & { assignedManager?: NamedRef | null };
  totals: {
    assignedDeals: number;
    activeDeals: number;
    pendingDeals: number;
    soldDeals: number;
    lostDeals: number;
    conversionRate: number;
    totalBellCount: number;
    bellEventsTriggered: number;
    managerHelpRequests: number;
    customerOnLotEvents: number;
    customerLeavingEvents: number;
    counterOffers: number;
    worksheetsCreated: number;
    approvedWorksheets: number;
  };
  financial: {
    totalSalesValue: number;
    totalGross: number;
    coverage: GrossAnalytics["coverage"];
  };
}

/** Reporting window + scope filters shared by dashboard/pipeline/deal queries. */
export interface AdminScopeParams {
  dateFrom?: string;
  dateTo?: string;
  managerId?: number | string;
  salespersonId?: number | string;
}

/** Deal-list status filter. Distinct from `AdminDealStatus`: the query takes
 *  lowercase filter tokens, the payload returns UPPER_SNAKE enums. */
export type AdminDealStatusFilter = "all" | "active" | "pending" | "sold" | "lost";

/** Account standing of the staff on a deal. `all` is expressed by omitting the
 *  parameter rather than sending a token. */
export type TeamStatusFilter = "active" | "suspended";

export interface AdminDealParams extends AdminScopeParams {
  status?: AdminDealStatusFilter;
  stage?: string;
  priority?: string;
  /** Narrows to deals whose manager/salesperson holds this account standing. */
  teamStatus?: TeamStatusFilter;
  search?: string;
  page?: number;
  limit?: number;
}

function scopeQuery(p: AdminScopeParams): Record<string, string | number | undefined> {
  return {
    dateFrom: p.dateFrom,
    dateTo: p.dateTo,
    managerId: p.managerId,
    salespersonId: p.salespersonId,
  };
}

// ---- Analytics -------------------------------------------------------------

export interface AnalyticsMetadata {
  scope?: string;
  reportingTimezone?: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  source?: string;
  periodField?: string;
  excludes?: string[];
  financialSource?: string;
  genericEngagementPercentage?: string;
}

export interface TrendPoint {
  date: string;
  count: number;
}

export interface BreakdownRow {
  id: number | null;
  name: string;
  count: number;
  bellEvents?: number;
  worksheets?: number;
  counterOffers?: number;
  totalDeals?: number;
  soldDeals?: number;
  lostDeals?: number;
  closeRate?: number;
}

export interface EngagementAnalytics {
  summary: {
    totalBellEvents: number;
    bellEventsPerDeal: number;
    bellVolumeByType: Record<string, number>;
    managerHelpVolume: number;
    managerEngagedVolume: number;
    customerOnLotVolume: number;
    customerLeavingVolume: number;
    escalationIncidence: number;
    testDriveCount: number;
    averageTestDriveDurationSeconds: number;
    worksheetSubmissions: number;
    worksheetApprovals: number;
    worksheetRejections: number;
    averageWorksheetReviewTurnaroundSeconds: number;
    counterOffers: number;
    counterOfferApprovalRate: number;
    counterOfferRejectionRate: number;
    averageCounterOfferReviewTurnaroundSeconds: number;
  };
  outcomeComparison: {
    sold: { deals: number; bellEvents: number; worksheets: number; counterOffers: number };
    lost: { deals: number; bellEvents: number; worksheets: number; counterOffers: number };
  };
  managerBreakdown: BreakdownRow[];
  salespersonBreakdown: BreakdownRow[];
  eventTrend: TrendPoint[];
  metadata: AnalyticsMetadata;
}

export interface GrossAnalytics {
  summary: {
    totalSalesValue: number;
    totalFrontGross: number;
    totalBackGross: number;
    totalGross: number;
    totalDiscounts: number;
    totalDownPayments: number;
    totalTradeInValue: number;
    totalTradeInPayoff: number;
    totalTaxesAndFees: number;
    averageSalePrice: number;
    averageGrossPerSoldDeal: number;
  };
  coverage: {
    soldDeals: number;
    dealsWithApprovedWorksheet: number;
    dealsMissingApprovedWorksheet: number;
    coveragePercentage: number;
  };
  trend: { date: string; totalGross?: number; totalSalesValue?: number; count?: number }[];
  managerBreakdown: BreakdownRow[];
  salespersonBreakdown: BreakdownRow[];
  financingBreakdown: {
    financedDeals: number;
    cashOrUnspecifiedDeals: number;
    averageFinanceMonths: number;
  };
  metadata: AnalyticsMetadata;
}

export interface LostDealAnalytics {
  summary: { totalLostDeals: number; totalSoldDeals: number; lossRate: number };
  trend: TrendPoint[];
  bySalesperson: BreakdownRow[];
  byManager: BreakdownRow[];
  byStage: { stage?: string; name?: string; count: number }[];
  byVehicle: { vehicle?: string; name?: string; count: number }[];
  signals: {
    withTestDrive: number;
    withoutTestDrive: number;
    withWorksheet: number;
    withoutWorksheet: number;
    withCounterOffer: number;
    withoutCounterOffer: number;
  };
  soldComparison: {
    soldDeals: number;
    withTestDrive: number;
    withWorksheet: number;
    withCounterOffer: number;
  };
  lostReasonDistribution: { reason?: string; name?: string; count: number }[];
  missingLostReason: number;
  metadata: AnalyticsMetadata;
}

export interface ScorecardAnalytics {
  summary: {
    totalCustomers: number;
    totalDeals: number;
    activeDeals: number;
    pendingDeals: number;
    soldDeals: number;
    lostDeals: number;
    closeRate: number;
    dealsClosedInPeriod: number;
    bellVolume: number;
    managerHelpVolume: number;
    customerLeavingVolume: number;
    escalations: number;
    testDrives: number;
    worksheetSubmissions: number;
    approvedWorksheets: number;
    counterOffers: number;
    approvedCounterOffers: number;
    recordedSalesValue: number;
    recordedTotalGross: number;
  };
  topManagers: BreakdownRow[];
  topSalespeople: BreakdownRow[];
  dealTrend: TrendPoint[];
  metadata: AnalyticsMetadata;
}

// ---- Leaderboards ----------------------------------------------------------

export interface LeaderboardRow {
  rank: number;
  id: number;
  displayName: string;
  profileImage: string | null;
  totalDeals: number;
  activeDeals: number;
  pendingDeals: number;
  soldDeals: number;
  lostDeals: number;
  closeRate: number;
}

// ---- Staff (managers / salespeople) ----------------------------------------

export interface StaffSummary {
  assignedSalespeople?: number;
  assignedDeals?: number;
  activeDeals: number;
  pendingDeals: number;
  soldDeals: number;
  lostDeals: number;
  closeRate: number;
  openBellCount?: number;
  totalDeals?: number;
  /** Salespeople report `totalBellCount`; the manager list uses `bellCount`. */
  totalBellCount?: number;
  bellCount?: number;
}

export interface StaffMember {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  profileImage: string | null;
  /** ACTIVE | SUSPENDED. The API returns this as `accountStatus`; `status` is
   *  kept as an optional alias so either spelling reads correctly — use
   *  `staffStatus()` rather than either field directly. */
  accountStatus?: string;
  status?: string;
  role: { code: string; label: string } | null;
  manager?: NamedRef | null;
  summary: StaffSummary;
  createdAt: string;
  updatedAt: string;
}

/** Account status for a staff member, tolerating either field spelling.
 *  Defaults to ACTIVE: the list endpoint has always sent a status, so an
 *  absent one means "not reported", never "suspended". */
export function staffStatus(
  m: Pick<StaffMember, "accountStatus" | "status"> | null | undefined,
): string {
  return m?.accountStatus ?? m?.status ?? "ACTIVE";
}

/** GET /api/admin/salespeople/{id} — the list shape plus profile fields, the
 *  assigned manager, and an `analytics` block that extends `summary` with
 *  counter-offer / worksheet counts. */
export interface SalespersonDetail extends StaffMember {
  note?: string | null;
  dateOfBirth?: string | null;
  assignedManager?: NamedRef | null;
  analytics?: StaffSummary & {
    totalCounterOffers?: number;
    totalWorksheets?: number;
    approvedWorksheets?: number;
  };
}

export interface ManagerAnalyticsTotals {
  totals: { totalManagers: number; activeManagers: number; suspendedManagers: number };
  averages: {
    dealsPerManager: number;
    activeDealsPerManager: number;
    completedDealsPerManager: number;
    activeBellsPerManager: number;
    activeSalespeoplePerManager: number;
    averageResponseTimeMinutes: number | null;
  };
}

export interface SalespersonAnalyticsTotals {
  totals: {
    totalSalespeople: number;
    activeSalespeople: number;
    suspendedSalespeople: number;
    totalAssignedDeals: number;
    activeDeals: number;
    soldDeals: number;
    lostDeals: number;
    conversionRate: number;
    totalBellCount: number;
    totalCounterOffers: number;
    totalWorksheets: number;
    approvedWorksheets: number;
  };
  financial: {
    totalSalesValue: number;
    totalGross: number;
    coverage: GrossAnalytics["coverage"];
  };
}

// ---- Alerts ----------------------------------------------------------------

export interface AdminAlert {
  id: number;
  category: string;
  status: string;
  priority: string;
  deal: { id: number; code: string; status: AdminDealStatus } | null;
  salesperson: NamedRef | null;
  manager: NamedRef | null;
  customer: NamedRef | null;
  triggeredBy: NamedRef | null;
  note: string | null;
  responseTime: number | null;
  createdAt: string;
  resolvedAt: string | null;
  resolvedBy: NamedRef | null;
}

export interface AlertParams extends AdminScopeParams {
  status?: "active" | "resolved" | "all";
  category?: string;
  priority?: string;
  page?: number;
  limit?: number;
  sortOrder?: "asc" | "desc";
}

// ---- Deal notes ------------------------------------------------------------

export interface DealNote {
  source: string;
  sourceId: number;
  text: string;
  author: NamedRef | null;
  createdAt: string;
}

// ---- Reports ---------------------------------------------------------------

/** GET /api/admin/reports — one descriptor per available report. Both `type`
 *  and the filter names are server-owned strings: the UI renders whichever
 *  filters a report declares rather than assuming a fixed set. */
export interface ReportDescriptor {
  type: string;
  name: string;
  description: string;
  supportedFormats: string[];
  /** Subset of: dateFrom, dateTo, salespersonId, managerId, status, stage,
   *  priority, search. Varies per report — `scorecard` supports no `search`,
   *  `salesperson-performance` supports neither `status` nor `search`. */
  supportedFilters: string[];
}

/** Filter keys a report can declare in `supportedFilters`. */
export type ReportFilterKey =
  | "dateFrom"
  | "dateTo"
  | "salespersonId"
  | "managerId"
  | "status"
  | "stage"
  | "priority"
  | "search";

/** Export formats the server offers per report via `supportedFormats`. */
export type ExportFormat = "csv" | "json" | "pdf" | "xlsx";

/** The type each format's bytes must carry for a browser to save rather than
 *  preview them. Used to re-label responses the server sends untyped. */
const EXPORT_MIME: Record<ExportFormat, string> = {
  csv: "text/csv",
  json: "application/json",
  pdf: "application/pdf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

export function isExportFormat(value: string): value is ExportFormat {
  return value in EXPORT_MIME;
}

/** Strips path separators and control characters — the filename comes from a
 *  response header, and `a.download` must not be steered outside the file name. */
function safeFilename(name: string): string {
  return name.replace(/[\u0000-\u001f\u007f]/g, "").replace(/[\\/]+/g, "_").trim();
}

/** Reads the download name out of Content-Disposition, preferring the RFC 5987
 *  `filename*=UTF-8''…` form when present, and falling back to a name derived
 *  from the report type. Also repairs a missing/incorrect extension so the file
 *  opens in the right application. */
function exportFilename(
  disposition: string | null,
  reportType: string,
  format: ExportFormat,
): string {
  const header = disposition ?? "";
  const encoded = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header);
  const plain = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(header);

  let name = "";
  if (encoded?.[1]) {
    try {
      name = decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ""));
    } catch {
      name = encoded[1].trim();
    }
  } else if (plain) {
    name = (plain[1] ?? plain[2] ?? "").trim();
  }

  name = safeFilename(name);
  if (!name) name = `${reportType}-report-${new Date().toISOString().slice(0, 10)}`;
  return name.toLowerCase().endsWith(`.${format}`) ? name : `${name}.${format}`;
}

/** POST /api/admin/reports/generate response.
 *
 *  Every report type returns this same envelope; only `summary` and `rows`
 *  differ in shape, and they differ per type (deals rows carry deal fields,
 *  financial rows carry money fields, engagement rows carry bell events). Both
 *  are therefore left open and rendered from whatever keys come back — this is
 *  what lets a report type the client has never seen still display correctly.
 *
 *  `rows` can legitimately be empty while `summary` is populated: the
 *  salesperson-performance sample returns rows: [] with a full summary. */
export interface GeneratedReport {
  reportType: string;
  generatedAt: string;
  /** Echo of the filters the server actually applied — `{}` when none. Can
   *  carry keys the caller never sent: a date range comes back with the
   *  resolved `effectiveUtcFrom` / `effectiveUtcTo` window alongside it. */
  filters: Record<string, unknown>;
  summary: Record<string, number | string | null>;
  rows: Record<string, unknown>[];
}

// ---- Configuration ----------------------------------------------------------

export interface ConfigGeneral {
  reportingTimezone: string;
}

export interface ConfigKpis {
  targetManagerResponseTimeMinutes: number;
  targetAverageBellsPerDeal: number;
  warningCloseRatePercent: number;
  strongCloseRatePercent: number;
}

export interface ConfigAlertRouting {
  customerOnLot: boolean;
  managerHelp: boolean;
  customerLeaving: boolean;
  counterOffer: boolean;
  supportTickets: boolean;
  bellSocketDelivery: boolean;
  bellPushDelivery: boolean;
}

export interface ConfigDataRetention {
  bellEventsDays: number;
  notificationsDays: number;
  supportTicketsDays: number;
  stalePushTokensDays: number;
}

export interface Configuration {
  general: ConfigGeneral;
  kpis: ConfigKpis;
  alertRouting: ConfigAlertRouting;
  dataRetention: ConfigDataRetention;
  updatedAt: string;
}

/** Multi-group update body — every group is optional and partially applied. */
export interface ConfigurationPatch {
  general?: Partial<ConfigGeneral>;
  kpis?: Partial<ConfigKpis>;
  alertRouting?: Partial<ConfigAlertRouting>;
  dataRetention?: Partial<ConfigDataRetention>;
}

export const configurationApi = {
  /** GET /api/admin/configuration — every group in one payload. */
  async get(signal?: AbortSignal): Promise<Configuration> {
    const env = await request<Configuration>("/api/admin/configuration", { signal });
    if (!env.data) throw new ApiError("Configuration unavailable", 502);
    return env.data;
  },

  /** PUT /api/admin/configuration — updates one or more groups at once and
   *  returns the full configuration. */
  async update(patch: ConfigurationPatch): Promise<Configuration> {
    const env = await request<Configuration>("/api/admin/configuration", {
      method: "PUT",
      body: patch,
    });
    if (!env.data) throw new ApiError("Configuration update failed", 502);
    return env.data;
  },

  /** PUT /api/admin/configuration/kpis — returns the KPI group only. */
  async updateKpis(input: Partial<ConfigKpis>): Promise<ConfigKpis | null> {
    const env = await request<ConfigKpis>("/api/admin/configuration/kpis", {
      method: "PUT",
      body: input,
    });
    return env.data ?? null;
  },

  /** PUT /api/admin/configuration/alert-routing. */
  async updateAlertRouting(input: Partial<ConfigAlertRouting>): Promise<ConfigAlertRouting | null> {
    const env = await request<ConfigAlertRouting>("/api/admin/configuration/alert-routing", {
      method: "PUT",
      body: input,
    });
    return env.data ?? null;
  },

  /** PUT /api/admin/configuration/data-retention. */
  async updateDataRetention(input: Partial<ConfigDataRetention>): Promise<ConfigDataRetention | null> {
    const env = await request<ConfigDataRetention>("/api/admin/configuration/data-retention", {
      method: "PUT",
      body: input,
    });
    return env.data ?? null;
  },

  /** POST /api/admin/configuration/reset — restores defaults for every group.
   *  Sends an empty JSON object rather than no body at all: without a body the
   *  client omits Content-Type, the server's JSON parser skips the request and
   *  its validator rejects the empty `req.body` with a 400. */
  async reset(): Promise<Configuration> {
    const env = await request<Configuration>("/api/admin/configuration/reset", {
      method: "POST",
      body: {},
    });
    if (!env.data) throw new ApiError("Configuration reset failed", 502);
    return env.data;
  },
};

// ---- Notifications -----------------------------------------------------------
//
// GET /api/notifications — the account's own delivered-notification inbox
// (support tickets, bell escalations, etc. — whatever `type` the server
// dispatches). Distinct from GET /api/admin/alerts: alerts are the store-wide
// bell event stream: notifications are what actually got delivered to this
// user, with server-owned read state.

/** Known so far; the server may dispatch other types, so this stays a string. */
export type NotificationType = "SUPPORT" | string;

/** Deep-link + correlation payload. Shape varies by `type`, so every field is
 *  optional — render only what a given notification actually carries. */
export interface NotificationData {
  route?: string;
  channel?: string;
  ticketId?: string;
  dealId?: string;
  eventType?: string;
}

export interface AppNotification {
  id: number;
  receiverId: number;
  type: NotificationType;
  title: string;
  body: string;
  imageUrl?: string | null;
  data?: NotificationData | null;
  isRead: boolean;
  readAt?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface NotificationParams {
  page?: number;
  limit?: number;
}

export const notificationsApi = {
  /** GET /api/notifications. */
  async list(
    params: NotificationParams = {},
    signal?: AbortSignal,
  ): Promise<Paginated<AppNotification>> {
    const env = await request<unknown>("/api/notifications", {
      query: { page: params.page, limit: params.limit },
      signal,
    });
    return toPaginated<AppNotification>(env);
  },

  /** GET /api/notifications/unread-count. */
  async unreadCount(signal?: AbortSignal): Promise<number> {
    const env = await request<{ count: number }>("/api/notifications/unread-count", { signal });
    return env.data?.count ?? 0;
  },

  /** PATCH /api/notifications/{id}/read. */
  async markRead(id: number | string): Promise<{ unreadCount: number } | null> {
    const env = await request<{ id: number; isRead: boolean; unreadCount: number }>(
      `/api/notifications/${id}/read`,
      { method: "PATCH", body: {} },
    );
    return env.data ?? null;
  },

  /** PATCH /api/notifications/read-all. */
  async markAllRead(): Promise<{ updatedCount: number; unreadCount: number } | null> {
    const env = await request<{ updatedCount: number; unreadCount: number }>(
      "/api/notifications/read-all",
      { method: "PATCH", body: {} },
    );
    return env.data ?? null;
  },

  /** DELETE /api/notifications/{id}. */
  async remove(id: number | string): Promise<{ unreadCount: number } | null> {
    const env = await request<{ id: number; unreadCount: number }>(`/api/notifications/${id}`, {
      method: "DELETE",
    });
    return env.data ?? null;
  },

  /**
   * DELETE /api/notifications — clears the caller's inbox.
   *
   * `readOnly` limits the sweep to already-read items, leaving unread ones in
   * place; omitting it clears everything. Scoped to the caller's own inbox, so
   * this never touches another account's notifications.
   */
  async clear(
    opts: { readOnly?: boolean } = {},
  ): Promise<{ deletedCount: number; unreadCount: number } | null> {
    const env = await request<{ deletedCount: number; unreadCount: number }>(
      "/api/notifications",
      {
        method: "DELETE",
        // Only sent when true — the endpoint treats the parameter's presence
        // as the flag, so `readOnly=false` would be ambiguous.
        query: opts.readOnly ? { readOnly: "true" } : undefined,
      },
    );
    return env.data ?? null;
  },
};

// ---- Admin notifications (broadcasts) ---------------------------------------
//
// GET /api/admin/notifications — the moderator-facing feed: broadcasts this
// account sent or received, distinct from GET /api/notifications (the
// account's own delivered inbox) and GET /api/admin/alerts (the store-wide
// bell event stream). "category" is the dispatch kind (BROADCAST so far;
// stays a string since the server may add more); "recipientType" is who a
// broadcast targeted.

export type AdminNotificationCategory = "BROADCAST" | string;
export type AdminNotificationPriority = "LOW" | "MEDIUM" | "HIGH" | string;
export type AdminNotificationRecipientType = "MANAGERS" | "SALESPEOPLE" | "ALL" | string;
export type AdminNotificationStatus = "UNREAD" | "READ";

export interface AdminNotification {
  id: number;
  title: string;
  message: string;
  category: AdminNotificationCategory;
  priority: AdminNotificationPriority;
  recipientType: AdminNotificationRecipientType;
  status: AdminNotificationStatus;
  createdAt: string;
  readAt: string | null;
  sender: NamedRef | null;
  reader: NamedRef | null;
}

export interface AdminNotificationParams {
  category?: AdminNotificationCategory;
  priority?: AdminNotificationPriority;
  /** Query takes lowercase ("unread"/"read"); responses return AdminNotificationStatus (uppercase). */
  status?: "unread" | "read";
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
}

/** POST /api/admin/notifications/broadcast body. `recipient` is lowercase,
 *  unlike the `recipientType` the list/read endpoints return. */
export interface BroadcastInput {
  title: string;
  message: string;
  recipient: "managers" | "salespeople" | "all" | string;
}

/** Converts the `recipientType` a listed broadcast carries ("MANAGERS") into
 *  the `recipient` the send endpoint expects ("managers"). The two endpoints
 *  disagree on case, so re-sending a listed broadcast has to translate.
 *
 *  Returns null for a type this client does not recognise rather than guessing
 *  — a wrong recipient would deliver the message to the wrong audience. */
export function broadcastRecipientOf(
  recipientType: AdminNotificationRecipientType,
): BroadcastInput["recipient"] | null {
  switch (String(recipientType).toUpperCase()) {
    case "MANAGERS":
      return "managers";
    case "SALESPEOPLE":
      return "salespeople";
    case "ALL":
      return "all";
    default:
      return null;
  }
}

export const adminNotificationsApi = {
  /** GET /api/admin/notifications. */
  async list(
    params: AdminNotificationParams = {},
    signal?: AbortSignal,
  ): Promise<Paginated<AdminNotification>> {
    const env = await request<unknown>("/api/admin/notifications", {
      query: {
        category: params.category,
        priority: params.priority,
        status: params.status,
        search: params.search,
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        sortOrder: params.sortOrder,
        page: params.page,
        limit: params.limit,
      },
      signal,
    });
    return toPaginated<AdminNotification>(env);
  },

  /** POST /api/admin/notifications/broadcast. */
  async broadcast(input: BroadcastInput): Promise<AdminNotification | null> {
    const env = await request<AdminNotification>("/api/admin/notifications/broadcast", {
      method: "POST",
      body: input,
    });
    return env.data ?? null;
  },

  /** PATCH /api/admin/notifications/{id}/read. */
  async markRead(
    id: number | string,
  ): Promise<{ id: number; status: AdminNotificationStatus; readAt: string; reader: NamedRef | null } | null> {
    const env = await request<{
      id: number;
      status: AdminNotificationStatus;
      readAt: string;
      reader: NamedRef | null;
    }>(`/api/admin/notifications/${id}/read`, { method: "PATCH", body: {} });
    return env.data ?? null;
  },
};

// ---- Static pages -----------------------------------------------------------
//
// GET/PUT /api/admin/pages — slug-addressed CMS pages (About Us, Terms, …)
// surfaced in the mobile apps. There is no POST: creation and update are the
// same upsert (PUT /api/admin/pages/{slug}), and the server bumps `version` on
// every write. The list endpoint omits `content` — only the detail endpoint
// returns the HTML body.

/** List row. `content` is absent here by design — fetch the detail for it. */
export interface PageSummary {
  id: number;
  slug: string;
  title: string;
  isPublished: boolean;
  version: number;
  updatedAt: string;
  createdAt?: string;
}

export interface PageDetail extends PageSummary {
  content: string;
  createdAt: string;
}

/** PUT /api/admin/pages/{slug} body. The slug travels in the URL, not here. */
export interface PageInput {
  title: string;
  content: string;
  isPublished: boolean;
}

export interface PageListParams {
  page?: number;
  limit?: number;
}

/** Lowercase, hyphen-separated, no leading/trailing hyphen — the slug is the
 *  resource identifier in the URL, so it is normalized before the request. */
export function toSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export const pagesApi = {
  /** GET /api/admin/pages — summaries only (no `content`). */
  async list(params: PageListParams = {}, signal?: AbortSignal): Promise<Paginated<PageSummary>> {
    const env = await request<unknown>("/api/admin/pages", {
      query: { page: params.page, limit: params.limit },
      signal,
    });
    return toPaginated<PageSummary>(env);
  },

  /** GET /api/admin/pages/{slug} — includes the HTML body. */
  async get(slug: string, signal?: AbortSignal): Promise<PageDetail> {
    const env = await request<PageDetail>(`/api/admin/pages/${encodeURIComponent(slug)}`, { signal });
    if (!env.data) throw new ApiError("Page not found", 404);
    return env.data;
  },

  /** PUT /api/admin/pages/{slug} — upsert. Creates when the slug is new,
   *  otherwise replaces the body and increments `version`. */
  async save(slug: string, input: PageInput): Promise<PageDetail | null> {
    const env = await request<PageDetail>(`/api/admin/pages/${encodeURIComponent(slug)}`, {
      method: "PUT",
      body: input,
    });
    return env.data ?? null;
  },
};

// ---- Help & training --------------------------------------------------------
//
// /api/admin/help-training — the in-app help library the mobile apps read.
// Four content types share one record shape, and which body field carries the
// payload depends on `contentType`:
//   ARTICLE / SLIDES  -> `content`  (HTML; SLIDES uses <section> per slide)
//   VIDEO / DOCUMENT  -> `mediaUrl` (absolute URL, or a server upload path)
// The unused field comes back null, so the form only sends the relevant one.

export type HelpContentType = "ARTICLE" | "SLIDES" | "VIDEO" | "DOCUMENT";

/** Confirmed by the API so far. Kept a string so a new server-side category
 *  never breaks the client. */
export type HelpCategory = "SALES" | "OPERATIONS" | string;

/** List row — `content` and `mediaUrl` are omitted by the list endpoint. */
export interface HelpItemSummary {
  id: number;
  title: string;
  description: string | null;
  category: HelpCategory;
  contentType: HelpContentType;
  thumbnailUrl: string | null;
  sortOrder: number;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface HelpItem extends HelpItemSummary {
  content: string | null;
  mediaUrl: string | null;
}

/** POST body. `content` and `mediaUrl` are mutually exclusive in practice —
 *  see the contentType table above. */
export interface CreateHelpItemInput {
  title: string;
  description?: string;
  content?: string;
  mediaUrl?: string;
  category: HelpCategory;
  contentType: HelpContentType;
  thumbnailUrl?: string;
  sortOrder?: number;
  isPublished?: boolean;
}

/** PUT accepts a partial — the sample updates title + sortOrder alone. */
export type UpdateHelpItemInput = Partial<CreateHelpItemInput>;

export interface HelpItemParams {
  category?: HelpCategory;
  contentType?: HelpContentType;
  search?: string;
  page?: number;
  limit?: number;
}

export const helpTrainingApi = {
  /** GET /api/admin/help-training. */
  async list(p: HelpItemParams = {}, signal?: AbortSignal): Promise<Paginated<HelpItemSummary>> {
    const env = await request<unknown>("/api/admin/help-training", {
      query: {
        category: p.category,
        contentType: p.contentType,
        search: p.search,
        page: p.page,
        limit: p.limit,
      },
      signal,
    });
    return toPaginated<HelpItemSummary>(env);
  },

  /** GET /api/admin/help-training/{id} — includes `content` / `mediaUrl`. */
  async get(id: number | string, signal?: AbortSignal): Promise<HelpItem> {
    const env = await request<HelpItem>(`/api/admin/help-training/${id}`, { signal });
    if (!env.data) throw new ApiError("Training item not found", 404);
    return env.data;
  },

  /** POST /api/admin/help-training. */
  async create(input: CreateHelpItemInput): Promise<HelpItem | null> {
    const env = await request<HelpItem>("/api/admin/help-training", {
      method: "POST",
      body: input,
    });
    return env.data ?? null;
  },

  /** PUT /api/admin/help-training/{id} — partial update. */
  async update(id: number | string, input: UpdateHelpItemInput): Promise<HelpItem | null> {
    const env = await request<HelpItem>(`/api/admin/help-training/${id}`, {
      method: "PUT",
      body: input,
    });
    return env.data ?? null;
  },

  /** PATCH /api/admin/help-training/{id}/status — publish / unpublish. */
  async setPublished(id: number | string, isPublished: boolean): Promise<HelpItem | null> {
    const env = await request<HelpItem>(`/api/admin/help-training/${id}/status`, {
      method: "PATCH",
      body: { isPublished },
    });
    return env.data ?? null;
  },

  /** DELETE /api/admin/help-training/{id}. */
  async remove(id: number | string): Promise<void> {
    await request<{ id: number; deleted: boolean }>(`/api/admin/help-training/${id}`, {
      method: "DELETE",
    });
  },
};

// ---- Support tickets --------------------------------------------------------

export type SupportTicketStatus = "OPEN" | "CLOSED" | string;

export interface SupportTicketAuthor {
  id: number;
  firstName: string;
  lastName: string;
  role?: string;
}

export interface SupportTicketReply {
  id: number | string;
  message: string;
  createdBy: SupportTicketAuthor | null;
  createdAt: string;
}

export interface SupportTicket {
  id: number | string;
  subject: string;
  message: string;
  status: SupportTicketStatus;
  createdAt: string;
  updatedAt?: string;
  closedAt?: string | null;
  createdBy: SupportTicketAuthor | null;
  assignedModerator?: SupportTicketAuthor | null;
  replyCount?: number;
  replies?: SupportTicketReply[];
  latestReply?: SupportTicketReply | null;
}

export interface SupportTicketParams {
  status?: "open" | "closed" | "all";
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export const supportApi = {
  /** GET /api/admin/support/tickets — server-side search, status/date filters, pagination. */
  async tickets(
    p: SupportTicketParams = {},
    signal?: AbortSignal,
  ): Promise<Paginated<SupportTicket>> {
    const env = await request<unknown>("/api/admin/support/tickets", {
      query: {
        status: p.status === "all" ? undefined : p.status,
        search: p.search,
        dateFrom: p.dateFrom,
        dateTo: p.dateTo,
        sortOrder: p.sortOrder,
        page: p.page,
        limit: p.limit,
      },
      signal,
    });
    return toPaginated<SupportTicket>(env);
  },

  /** GET /api/admin/support/tickets/{id}. */
  async ticket(id: number | string, signal?: AbortSignal): Promise<SupportTicket | null> {
    const env = await request<SupportTicket>(`/api/admin/support/tickets/${id}`, { signal });
    return env.data ?? null;
  },

  /** POST /api/admin/support/tickets/{id}/reply. */
  async reply(id: number | string, message: string): Promise<void> {
    await request<unknown>(`/api/admin/support/tickets/${id}/reply`, {
      method: "POST",
      body: { message },
    });
  },

  /** PATCH /api/admin/support/tickets/{id}/close. */
  async close(id: number | string): Promise<void> {
    await request<unknown>(`/api/admin/support/tickets/${id}/close`, {
      method: "PATCH",
      body: {},
    });
  },
};

export const adminApi = {
  /** GET /api/admin/dashboard — store-wide KPI snapshot. */
  async dashboard(params: AdminScopeParams = {}, signal?: AbortSignal): Promise<AdminDashboard> {
    const env = await request<AdminDashboard>("/api/admin/dashboard", {
      query: scopeQuery(params),
      signal,
    });
    if (!env.data) throw new ApiError("Dashboard unavailable", 502);
    return env.data;
  },

  /** GET /api/admin/dashboard/filters — selectable managers / salespeople / enums. */
  async dashboardFilters(signal?: AbortSignal): Promise<AdminFilters | null> {
    const env = await request<AdminFilters>("/api/admin/dashboard/filters", { signal });
    return env.data ?? null;
  },

  /** GET /api/admin/pipeline — deal counts by status and stage.
   *  Takes no query parameters: the endpoint rejects unrecognized keys. */
  async pipeline(signal?: AbortSignal): Promise<AdminPipeline> {
    const env = await request<AdminPipeline>("/api/admin/pipeline", { signal });
    if (!env.data) throw new ApiError("Pipeline unavailable", 502);
    return env.data;
  },

  /** GET /api/admin/deals — server-side search + pagination. */
  async deals(params: AdminDealParams = {}, signal?: AbortSignal): Promise<Paginated<AdminDeal>> {
    const env = await request<unknown>("/api/admin/deals", {
      query: {
        ...scopeQuery(params),
        status: params.status,
        stage: params.stage,
        priority: params.priority,
        teamStatus: params.teamStatus,
        search: params.search,
        page: params.page,
        limit: params.limit,
      },
      signal,
    });
    return toPaginated<AdminDeal>(env);
  },

  /** GET /api/admin/deals/filters. */
  async dealFilters(signal?: AbortSignal): Promise<AdminFilters | null> {
    const env = await request<AdminFilters>("/api/admin/deals/filters", { signal });
    return env.data ?? null;
  },

  /** GET /api/admin/deals/{id} — deal, customer, audit timeline, bell timeline. */
  async deal(id: number | string, signal?: AbortSignal): Promise<AdminDealDetail> {
    const env = await request<AdminDealDetail>(`/api/admin/deals/${id}`, { signal });
    if (!env.data) throw new ApiError("Deal not found", 404);
    return env.data;
  },

  /** GET /api/admin/deals/{id}/bells — immutable bell event stream. */
  async dealBells(id: number | string, signal?: AbortSignal): Promise<AdminBellEvent[]> {
    const env = await request<AdminBellEvent[]>(`/api/admin/deals/${id}/bells`, { signal });
    return Array.isArray(env.data) ? env.data : [];
  },

  /** GET /api/admin/customers. */
  async customers(
    params: { search?: string; page?: number; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<Paginated<AdminCustomer>> {
    const env = await request<unknown>("/api/admin/customers", {
      query: { search: params.search, page: params.page, limit: params.limit },
      signal,
    });
    return toPaginated<AdminCustomer>(env);
  },

  /** GET /api/admin/customers/{id} — profile, licence scan and deal history. */
  async customer(id: number | string, signal?: AbortSignal): Promise<AdminCustomerDetail | null> {
    const env = await request<AdminCustomerDetail>(`/api/admin/customers/${id}`, { signal });
    return env.data ?? null;
  },

  /** PUT /api/admin/deals/{id} — status, priority, manager assignment, note. */
  async updateDeal(id: number | string, input: UpdateDealInput): Promise<void> {
    await request<unknown>(`/api/admin/deals/${id}`, { method: "PUT", body: compact(input) });
  },

  async dealDocuments(id: number | string, signal?: AbortSignal): Promise<DealDocument[]> {
    const env = await request<DealDocument[]>(`/api/admin/deals/${id}/documents`, { signal });
    return Array.isArray(env.data) ? env.data : [];
  },

  async dealWorksheets(id: number | string, signal?: AbortSignal): Promise<DealWorksheet[]> {
    const env = await request<DealWorksheet[]>(`/api/admin/deals/${id}/worksheets`, { signal });
    return Array.isArray(env.data) ? env.data : [];
  },

  async dealCounterOffers(id: number | string, signal?: AbortSignal): Promise<DealCounterOffer[]> {
    const env = await request<DealCounterOffer[]>(`/api/admin/deals/${id}/counter-offers`, { signal });
    return Array.isArray(env.data) ? env.data : [];
  },

  // ---- Analytics ----------------------------------------------------------

  async engagementAnalytics(p: AdminScopeParams = {}, signal?: AbortSignal) {
    const env = await request<EngagementAnalytics>("/api/admin/analytics/engagement", {
      query: scopeQuery(p),
      signal,
    });
    if (!env.data) throw new ApiError("Engagement analytics unavailable", 502);
    return env.data;
  },

  async grossAnalytics(p: AdminScopeParams = {}, signal?: AbortSignal) {
    const env = await request<GrossAnalytics>("/api/admin/analytics/gross", {
      query: scopeQuery(p),
      signal,
    });
    if (!env.data) throw new ApiError("Financial analytics unavailable", 502);
    return env.data;
  },

  async lostDealAnalytics(p: AdminScopeParams = {}, signal?: AbortSignal) {
    const env = await request<LostDealAnalytics>("/api/admin/analytics/lost-deals", {
      query: scopeQuery(p),
      signal,
    });
    if (!env.data) throw new ApiError("Lost-deal analytics unavailable", 502);
    return env.data;
  },

  async scorecard(p: AdminScopeParams = {}, signal?: AbortSignal) {
    const env = await request<ScorecardAnalytics>("/api/admin/analytics/scorecard", {
      query: scopeQuery(p),
      signal,
    });
    if (!env.data) throw new ApiError("Scorecard unavailable", 502);
    return env.data;
  },

  // ---- Leaderboards -------------------------------------------------------

  async managerLeaderboard(
    p: AdminScopeParams & { page?: number; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<Paginated<LeaderboardRow>> {
    const env = await request<unknown>("/api/admin/leaderboards/managers", {
      query: { ...scopeQuery(p), page: p.page, limit: p.limit },
      signal,
    });
    return toPaginated<LeaderboardRow>(env);
  },

  async salespersonLeaderboard(
    p: AdminScopeParams & { page?: number; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<Paginated<LeaderboardRow>> {
    const env = await request<unknown>("/api/admin/leaderboards/salespeople", {
      query: { ...scopeQuery(p), page: p.page, limit: p.limit },
      signal,
    });
    return toPaginated<LeaderboardRow>(env);
  },

  // ---- Managers -----------------------------------------------------------

  /** GET /api/admin/managers. `search` matches names; `email` is a separate
   *  exact-match parameter. `status` takes lowercase (active/suspended) while
   *  each record returns it uppercase. */
  async managers(
    p: {
      search?: string;
      email?: string;
      status?: string;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
    } = {},
    signal?: AbortSignal,
  ): Promise<Paginated<StaffMember>> {
    const env = await request<unknown>("/api/admin/managers", { query: { ...p }, signal });
    return toPaginated<StaffMember>(env);
  },

  async manager(id: number | string, signal?: AbortSignal): Promise<ManagerDetail | null> {
    const env = await request<ManagerDetail>(`/api/admin/managers/${id}`, { signal });
    return env.data ?? null;
  },

  async createManager(input: CreateStaffInput): Promise<StaffMember | null> {
    const env = await request<StaffMember>("/api/admin/managers", { method: "POST", body: input });
    return env.data ?? null;
  },

  /** PUT /api/admin/managers/{id}.
   *
   *  The response is deliberately discarded: unlike every other manager
   *  endpoint it returns a flat user record (`accountStatus`, `role: "admin"`,
   *  no `summary`) rather than a StaffMember, so callers refetch the list
   *  instead of merging this payload into it. */
  async updateManager(id: number | string, input: UpdateStaffInput): Promise<void> {
    await request<unknown>(`/api/admin/managers/${id}`, { method: "PUT", body: compact(input) });
  },

  /** PATCH /api/admin/managers/{id}/status — ACTIVE | SUSPENDED. */
  async setManagerStatus(id: number | string, status: "ACTIVE" | "SUSPENDED"): Promise<void> {
    await request<unknown>(`/api/admin/managers/${id}/status`, { method: "PATCH", body: { status } });
  },

  async deleteManager(id: number | string): Promise<void> {
    await request<null>(`/api/admin/managers/${id}`, { method: "DELETE" });
  },

  async managerAnalytics(
    id: number | string,
    signal?: AbortSignal,
  ): Promise<ManagerIndividualAnalytics | null> {
    const env = await request<ManagerIndividualAnalytics>(`/api/admin/managers/${id}/analytics`, {
      signal,
    });
    return env.data ?? null;
  },

  async managerTotals(signal?: AbortSignal): Promise<ManagerAnalyticsTotals | null> {
    const env = await request<ManagerAnalyticsTotals>("/api/admin/managers/analytics", { signal });
    return env.data ?? null;
  },

  /** GET /api/admin/managers/{id}/deals — status, stage, priority and search
   *  combine (each narrows the result further). */
  async managerDeals(
    id: number | string,
    p: AdminDealParams = {},
    signal?: AbortSignal,
  ): Promise<Paginated<AdminDeal>> {
    const env = await request<unknown>(`/api/admin/managers/${id}/deals`, {
      query: {
        status: p.status,
        stage: p.stage,
        priority: p.priority,
        search: p.search,
        page: p.page,
        limit: p.limit,
      },
      signal,
    });
    return toPaginated<AdminDeal>(env);
  },

  // ---- Salespeople --------------------------------------------------------

  async salespeople(
    p: { search?: string; status?: string; page?: number; limit?: number; sortOrder?: string } = {},
    signal?: AbortSignal,
  ): Promise<Paginated<StaffMember>> {
    const env = await request<unknown>("/api/admin/salespeople", { query: { ...p }, signal });
    return toPaginated<StaffMember>(env);
  },

  /** GET /api/admin/salespeople/{id} — richer than the list row: adds note,
   *  dateOfBirth, assignedManager and the `analytics` block. */
  async salesperson(id: number | string, signal?: AbortSignal): Promise<SalespersonDetail | null> {
    const env = await request<SalespersonDetail>(`/api/admin/salespeople/${id}`, { signal });
    return env.data ?? null;
  },

  async createSalesperson(input: CreateStaffInput): Promise<StaffMember | null> {
    const env = await request<StaffMember>("/api/admin/salespeople", { method: "POST", body: input });
    return env.data ?? null;
  },

  async updateSalesperson(id: number | string, input: UpdateStaffInput): Promise<void> {
    await request<unknown>(`/api/admin/salespeople/${id}`, { method: "PUT", body: compact(input) });
  },

  async setSalespersonStatus(id: number | string, status: "ACTIVE" | "SUSPENDED"): Promise<void> {
    await request<unknown>(`/api/admin/salespeople/${id}/status`, {
      method: "PATCH",
      body: { status },
    });
  },

  async deleteSalesperson(id: number | string): Promise<void> {
    await request<null>(`/api/admin/salespeople/${id}`, { method: "DELETE" });
  },

  async salespersonAnalytics(
    id: number | string,
    signal?: AbortSignal,
  ): Promise<SalespersonIndividualAnalytics | null> {
    const env = await request<SalespersonIndividualAnalytics>(
      `/api/admin/salespeople/${id}/analytics`,
      { signal },
    );
    return env.data ?? null;
  },

  async salespersonTotals(signal?: AbortSignal): Promise<SalespersonAnalyticsTotals | null> {
    const env = await request<SalespersonAnalyticsTotals>("/api/admin/salespeople/analytics", {
      signal,
    });
    return env.data ?? null;
  },

  /** GET /api/admin/salespeople/{id}/deals — same filter set as the manager
   *  equivalent: status, stage, priority and search all combine. */
  async salespersonDeals(
    id: number | string,
    p: AdminDealParams = {},
    signal?: AbortSignal,
  ): Promise<Paginated<AdminDeal>> {
    const env = await request<unknown>(`/api/admin/salespeople/${id}/deals`, {
      query: {
        status: p.status,
        stage: p.stage,
        priority: p.priority,
        search: p.search,
        page: p.page,
        limit: p.limit,
      },
      signal,
    });
    return toPaginated<AdminDeal>(env);
  },

  // ---- Alerts -------------------------------------------------------------

  async alerts(p: AlertParams = {}, signal?: AbortSignal): Promise<Paginated<AdminAlert>> {
    const env = await request<unknown>("/api/admin/alerts", {
      query: {
        status: p.status,
        category: p.category,
        priority: p.priority,
        page: p.page,
        limit: p.limit,
        sortOrder: p.sortOrder,
      },
      signal,
    });
    return toPaginated<AdminAlert>(env);
  },

  async alert(id: number | string, signal?: AbortSignal): Promise<AdminAlert | null> {
    const env = await request<AdminAlert>(`/api/admin/alerts/${id}`, { signal });
    return env.data ?? null;
  },

  /** PATCH /api/admin/alerts/{id}/resolve — audited manager action. */
  async resolveAlert(id: number | string): Promise<void> {
    await request<null>(`/api/admin/alerts/${id}/resolve`, { method: "PATCH" });
  },

  // ---- Deal notes ---------------------------------------------------------

  async dealNotes(id: number | string, signal?: AbortSignal): Promise<DealNote[]> {
    const env = await request<DealNote[]>(`/api/admin/deals/${id}/notes`, { signal });
    return Array.isArray(env.data) ? env.data : [];
  },

  // ---- Reports ------------------------------------------------------------

  async reports(signal?: AbortSignal): Promise<ReportDescriptor[]> {
    const env = await request<ReportDescriptor[]>("/api/admin/reports", { signal });
    return Array.isArray(env.data) ? env.data : [];
  },

  /** POST /api/admin/reports/generate — renders a report as JSON.
   *
   *  Filters go in a nested `filters` object, not flat on the body:
   *    { reportType: "financial", filters: { dateFrom, dateTo } }
   *  `compact` drops empty values so an unset filter is omitted rather than
   *  sent blank, and `filters` itself is omitted when nothing is set. Callers
   *  should pass only what a report's `supportedFilters` declares. */
  async generateReport(
    reportType: string,
    filters: AdminDealParams = {},
    signal?: AbortSignal,
  ): Promise<GeneratedReport | null> {
    const applied = compact({
      ...scopeQuery(filters),
      status: filters.status,
      stage: filters.stage,
      priority: filters.priority,
      search: filters.search,
    });

    const env = await request<GeneratedReport>("/api/admin/reports/generate", {
      method: "POST",
      body: {
        reportType,
        ...(Object.keys(applied).length > 0 ? { filters: applied } : {}),
      },
      signal,
    });
    return env.data ?? null;
  },

  /** GET /api/admin/reports/export — streams a file and requires the bearer
   *  token, so it can't be a plain <a href>. Returns the blob + filename from
   *  Content-Disposition for the caller to save.
   *
   *  Unlike POST /generate, this takes its filters as flat query parameters
   *  (`?reportType=financial&format=csv&dateFrom=…`), not a nested object. */
  async exportReport(
    reportType: string,
    format: ExportFormat,
    filters: AdminDealParams = {},
    signal?: AbortSignal,
  ): Promise<{ blob: Blob; filename: string }> {
    const url = new URL(`${BASE_URL}/api/admin/reports/export`);
    url.searchParams.set("reportType", reportType);
    url.searchParams.set("format", format);
    const query: Record<string, string | number | undefined> = {
      ...scopeQuery(filters),
      status: filters.status,
      stage: filters.stage,
      priority: filters.priority,
      search: filters.search,
    };
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
    }

    const token = getToken();
    let res: Response;
    try {
      res = await fetch(url.toString(), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      throw new ApiError("Cannot reach the server. Check your connection and try again.", 0);
    }

    if (res.status === 401) clearSession();
    if (!res.ok) throw new ApiError(`Export failed (${res.status})`, res.status);

    // The server streams the body but does not always label it: a PDF served as
    // application/octet-stream (or a CSV as text/plain) is previewed inline by
    // the browser instead of downloaded. Re-wrap the bytes with the MIME type
    // the requested format implies so saveBlob always produces a file.
    const raw = await res.blob();
    const served = raw.type.split(";")[0].trim().toLowerCase();
    const blob =
      served === EXPORT_MIME[format] ? raw : new Blob([raw], { type: EXPORT_MIME[format] });

    return {
      blob,
      filename: exportFilename(res.headers.get("Content-Disposition"), reportType, format),
    };
  },
};

/** Saves an exported report through a temporary object URL.
 *
 *  The object URL is revoked on the next frame rather than immediately: Firefox
 *  and Safari abort the download if the URL dies in the same tick as the click. */
export function saveBlob(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

export { BASE_URL };
